import { useState } from "react";
import { Copy, Check, Share2 } from "lucide-react";
import { useToast } from "@/components/ux/Toast";
import { generateReferralLink } from "@/lib/referral";
import tracking from "@/lib/tracking";
import { useT } from "@/lib/i18n";
import { copyToClipboard } from "@/lib/platform/clipboard";

interface ReferralLinkCopyProps {
  referralId: string;
  variant?: "default" | "compact";
}

export default function ReferralLinkCopy({ referralId, variant = "default" }: ReferralLinkCopyProps) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const toast = useToast();
  const referralLink = generateReferralLink(referralId);
  const canShare = typeof navigator !== "undefined" && !!navigator.share;

  const copyReferralLink = async () => {
    const success = await copyToClipboard(referralLink);
    if (success) {
      tracking.copyReferralLink();
      setCopied(true);
      toast.success(t('referral.linkCopied'));
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error(t('referral.copyFailed'));
    }
  };

  const shareReferralLink = async () => {
    try {
      await navigator.share({
        title: t('referral.shareTitle'),
        text: t('referral.shareText'),
        url: referralLink,
      });
      tracking.shareReferralLink(); // Track share as well
    } catch (err) {
      // User cancelled or share failed - don't show error for cancel
      if ((err as Error).name !== "AbortError") {
        toast.error(t('referral.shareFailed'));
      }
    }
  };

  if (variant === "compact") {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 bg-type-50 dark:bg-neutral-800/80 border border-type-100 dark:border-neutral-700 rounded-lg px-3 py-2">
          <p className="font-mono text-sm font-bold tracking-wider text-primary-600 dark:text-primary-400 flex-1 truncate">
            {referralId}
          </p>
          <button
            onClick={copyReferralLink}
            className="p-1.5 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded transition-colors flex-shrink-0"
            title={t('referral.copyLink')}
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4 text-primary-500" />
            )}
          </button>
          {canShare && (
            <button
              onClick={shareReferralLink}
              className="p-1.5 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded transition-colors flex-shrink-0"
              title={t('referral.share')}
            >
              <Share2 className="w-4 h-4 text-primary-500" />
            </button>
          )}
        </div>
        <p className="text-xs text-dark-muted dark:text-neutral-500 break-all text-center">
          {referralLink}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="bg-type-50 dark:bg-neutral-800/80 border border-type-100 dark:border-neutral-700 rounded-xl p-4 text-center">
        <p className="font-mono text-2xl font-bold tracking-widest text-primary-600 dark:text-primary-400">
          {referralId}
        </p>
      </div>
      
      <p className="text-xs text-dark-muted dark:text-neutral-500 break-all text-center">
        {referralLink}
      </p>
      
      <div className={`flex gap-3 ${canShare ? "" : "flex-col"}`}>
        <button
          onClick={copyReferralLink}
          className="btn btn-primary flex-1 flex items-center justify-center gap-2"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              {t('referral.copied')}
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              {t('referral.copyLink')}
            </>
          )}
        </button>
        {canShare && (
          <button
            onClick={shareReferralLink}
            className="btn btn-ghost border border-primary-500 flex-1 flex items-center justify-center gap-2"
          >
            <Share2 className="w-4 h-4" />
            {t('referral.share')}
          </button>
        )}
      </div>
    </div>
  );
}

